import { Dropdown, DropdownItem, DropdownPosition, KebabToggle, Spinner } from '@patternfly/react-core';
import React, { useEffect, useState } from 'react';
import { ClusterRepresentation, useCreateBillingSession, useGetSubscriptionInfo } from 'src/query/deployments';
import toast from 'react-hot-toast';
import { API } from 'src/api';
import { ConfirmationDialog } from '@app/components/ConfirmationDialog/confirmation-dialog';

type Props = {
  refetch: () => void;
  cluster: ClusterRepresentation;
};

export const ClusterActionDropdown = ({ cluster, refetch }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [deleteCluster, setDeleteCluster] = useState<ClusterRepresentation | null>(null);
  const [isDeletingCluster, setisDeletingCluster] = useState(false);

  const onToggle = (isOpen: boolean) => {
    setIsOpen(isOpen);
  };

  const onFocus = () => {
    const element = document.getElementById('toggle-kebab');
    element?.focus();
  };

  const onSelect = () => {
    setIsOpen(false);
    onFocus();
  };

  const { mutate, data: billingLink, isSuccess: isBillingLinkSuccess } = useCreateBillingSession();
  const { isSuccess: isSubscriptionSuccess } = useGetSubscriptionInfo({
    pathParams: { clusterId: cluster.id, realm: 'self' },
  });

  useEffect(() => {
    if (isBillingLinkSuccess && billingLink.link) {
      window.location.href = billingLink.link;
    }
  }, [billingLink]);

  const goToBillingLink = () => {
    if (isSubscriptionSuccess) {
      mutate({ pathParams: { clusterId: cluster.id, realm: 'self' } });
    }
  };

  const dropdownItems = [
    <DropdownItem
      key={`${!isSubscriptionSuccess ? 'disabled' : ''} link`}
      isDisabled={!isSubscriptionSuccess}
      onClick={goToBillingLink}
    >
      Billing
    </DropdownItem>,
    <DropdownItem key="action" component="button" style={{ color: 'red' }} onClick={() => setDeleteCluster(cluster)}>
      Delete cluster
    </DropdownItem>,
  ];

  const deleteACluster = async () => {
    setisDeletingCluster(true);
    toast('Deleting cluster. Please standby as this can take a few moments...');
    if (!deleteCluster) {
      return;
    }
    try {
      const deletedCluster = await API.Orgs.deleteCluster({ clusterId: deleteCluster?.id });

      if (deletedCluster.ok) {
        toast.success('Cluster deleted.');
        setDeleteCluster(null);
        refetch();
        return;
      } else {
        console.log('[Error] Could not delete cluster.');
      }
    } catch (e) {
      console.log('[Error] Was not able to delete cluster.');
    }
    setisDeletingCluster(false);
    toast.error('Error trying to delete cluster. Please try again.');
  };

  return (
    <>
      {deleteCluster && (
        <ConfirmationDialog
          isOpen={!!deleteCluster}
          title={`Delete this cluster: ${deleteCluster.name}`}
          body="Deleting this cluster will remove all data associated with this realm. This action cannot be undone."
          confirmText={
            isDeletingCluster ? (
              <div>
                <Spinner
                  isSVG
                  size="md"
                  aria-label="Contents of the small example"
                  style={{ '--pf-c-spinner--Color': 'white' }}
                />{' '}
                Deleting cluster...
              </div>
            ) : (
              'Delete'
            )
          }
          confirmVariant="danger"
          onModalCancel={() => setDeleteCluster(null)}
          onModalConfirm={() => deleteACluster()}
          disableConfirm={isDeletingCluster}
        />
      )}
      <Dropdown
        onSelect={onSelect}
        toggle={<KebabToggle id="toggle-kebab" onToggle={onToggle} />}
        isOpen={isOpen}
        isPlain
        dropdownItems={dropdownItems}
        position={DropdownPosition.right}
      />
    </>
  );
};
